import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import constants from 'app/constants'
import fetchJSONData from 'app/util/fetchJSONData'
import Content from 'components/content/Content/Content'
import Layout from 'components/core/Layout/Layout'
import ButtonTab from 'components/elements/ButtonTab/ButtonTab'
import Spinner from 'components/elements/Spinner/Spinner'
import Modal from 'components/interface/Modal/Modal'
import { useModalContext } from 'components/interface/Modal/provider/ModalProvider'
import useDelayedActiveState from 'lib/react/hooks/useDelayedActiveState'
import styles from './ModalPrivacyTerms.module.scss'

let cache = null

/**
 * The `ModalPrivacyTerms` handles the rendering of
 * the sign up modal
 *
 * @returns {React.ReactElement} the element
 */
function ModalPrivacyTerms () {
  const { isModalContentActive } = useModalContext()
  const isActive = useDelayedActiveState(isModalContentActive, 0.2, 0)
  const [data, setData] = useState(cache)
  const [error, setError] = useState(null)
  const [currentTab, setCurrentTab] = useState('privacy')
  const isMounted = useRef(false)
  const bodyRef = useRef()

  const title = useMemo(() => <h1>Privacy & Terms</h1>, [])

  const loadData = useCallback(async () => {
    const { data, success } = await fetchJSONData(
      `${constants.PATH_DATA}/privacy_terms.json?ver=${constants.CACHE_BUST_VERSION}`
    )

    if (!isMounted.current) {
      // No longer mounted
      return
    }

    if (!success) {
      setError('There was a problem loading the data. Please try again later.')

      return
    }

    const res = data

    cache = res

    setData(cache)
  }, [])

  const output = useMemo(() => {
    if (!data && !error) {
      return <Spinner />
    }

    if (error) {
      return (
        <div className={styles.error}>
          <p>{error}</p>
        </div>
      )
    }

    if (currentTab === 'privacy') {
      return <Content items={data.privacy} />
    }

    return <Content items={data.terms} />
  }, [data, error, currentTab])

  /**
   * Effect: When the `currentTab` changes, reset the scroll
   */
  useEffect(() => {
    bodyRef.current.scrollTo(0, 0)
  }, [currentTab])

  /**
   * Effect: On mount
   */
  useEffect(() => {
    isMounted.current = true

    if (!cache) {
      loadData()
    }

    return () => {
      isMounted.current = false
    }
  }, [])

  return (
    <Modal.Container size="small">
      <Modal.Header>{title}</Modal.Header>
      <Modal.Tabs>
        <ButtonTab
          isSelected={currentTab === 'privacy'}
          size="medium"
          isActive={false}
          isTransitionActive={isActive}
          index={1}
          onClick={() => setCurrentTab('privacy')}
        >
          Privacy
        </ButtonTab>
        <ButtonTab
          isSelected={currentTab === 'terms'}
          size="medium"
          isActive={false}
          isTransitionActive={isActive}
          index={2}
          onClick={() => setCurrentTab('terms')}
        >
          Terms
        </ButtonTab>
      </Modal.Tabs>
      <Modal.Body ref={bodyRef} className={styles.content}>
        <Layout.Body justify="center">{output}</Layout.Body>
        {/* <Content items={appData?.['privacy-terms']?.content} /> */}
      </Modal.Body>
      <Modal.Footer />
    </Modal.Container>
  )
}

// Memoize
export default memo(ModalPrivacyTerms)
